// Shown as Flagged since ENTRAID-305; ENTRAID-304 redesigns it.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import WatchAnswers from '../../components/WatchAnswers.jsx';
import WatchEventDetails from '../../components/WatchEventDetails.jsx';
import WatchGroupLinks from '../../components/WatchGroupLinks.jsx';
import WatchPreferencesForm from '../../components/WatchPreferencesForm.jsx';
import { watchQuestion } from '../../../../shared/watch-question.mjs';
import { ANSWER_LABEL, AUDIENCE_LABEL, activityPath, formatWhen, notificationsPath, statusText } from '../../lib/watch-format.js';
import { ExpectedWord, SeverityWord } from '../../components/StatusWord.jsx';
import { adminAnswerMode, noRecipientText } from '../../lib/watch-admin-answer.js';

const NOT_AVAILABLE = 'This event is not available to you.';
const EXPIRED_TEXT = 'The time to answer this notice has passed, so your answer was not recorded.';
const GONE_TEXT = 'This notice is no longer active, so your answer was not recorded.';
const CLOSED_TEXT = 'This notice can no longer be answered here.';
const ALREADY_TEXT = 'This notice was already answered, so your new answer was not recorded.';
const SAVED_TEXT = 'Saved. You will get a confirmation email shortly if you do not already have one for this setting.';

// The portal answers 410 { state: 'expired' } when the 30-day window has passed.
function answerError(e) {
  if (e?.status === 410) return e?.body?.state === 'expired' ? EXPIRED_TEXT : GONE_TEXT;
  if (e?.status === 409) return ALREADY_TEXT;
  if (e?.status === 404) return NOT_AVAILABLE;
  return e?.message || 'The answer could not be recorded.';
}

function Preferences({ customerId, notificationId }) {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  // The notification whose preferences are showing; a response for any other is dropped.
  const keyRef = useRef(null);
  const key = `${customerId}|${notificationId}`;

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const d = await api.getWatchPreferences(customerId, notificationId);
      if (keyRef.current === key) setData(d);
    } catch (e) {
      if (keyRef.current === key) setLoadError(e?.message || 'Preferences could not be loaded.');
    }
  }, [customerId, notificationId, key]);
  useEffect(() => {
    keyRef.current = key;
    setData(null);
    setMessage(null);
    setSaving(false);
    load();
    return () => { keyRef.current = null; };
  }, [key, load]);

  if (!data) {
    if (loadError) {
      return (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ipc-bad" role="alert">{loadError}</p>
          <button type="button" className="btn-secondary px-3 py-1 text-sm" onClick={load}>Try again</button>
        </div>
      );
    }
    return <p className="text-sm text-ipc-muted" role="status">Loading</p>;
  }
  if (Object.keys(data.model ?? {}).length === 0) return <p className="text-sm text-ipc-muted">These notices cannot be changed: they are about your own account or about Watch itself.</p>;

  async function save(categories) {
    setSaving(true);
    setMessage(null);
    try {
      const r = await api.putWatchPreferences(customerId, notificationId, categories);
      if (keyRef.current !== key) return;
      setData((d) => ({ ...d, categories: r?.categories ?? { ...d.categories, ...categories } }));
      setMessage({ ok: true, text: SAVED_TEXT });
    } catch (e) {
      if (keyRef.current === key) setMessage({ ok: false, text: e?.message || 'Preferences could not be saved.' });
    } finally {
      if (keyRef.current === key) setSaving(false);
    }
  }
  return (
    <div className="space-y-2">
      <WatchPreferencesForm key={JSON.stringify(data.categories ?? {})} model={data.model} categories={data.categories} onSave={save} saving={saving} />
      {message ? <p className={`text-sm ${message.ok ? 'text-ipc-ink' : 'text-ipc-bad'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</p> : null}
    </div>
  );
}

/**
 * The authenticated event page (spec 9.3, 9.4): the admin's event view and the
 * page every recipient's email link opens. The link only preselects an answer;
 * one Confirm records it (a GET never changes anything). Operators never see
 * an event: the portal refuses them, and this page does not ask.
 */
export default function WatchEventPage({ customerId, isAdmin = false, operatorAccount = false }) {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const notificationId = params.get('n') || undefined;
  if (operatorAccount) {
    return <div className="card text-sm text-ipc-ink" role="status">Event details are shown to the people who received the notice and to the customer's administrators. Operator accounts do not see them.</div>;
  }
  return <EventView key={`${customerId}|${eventId}|${notificationId ?? ''}`} customerId={customerId} eventId={eventId} notificationId={notificationId} isAdmin={isAdmin} />;
}

function EventView({ customerId, eventId, notificationId, isAdmin }) {
  const [params] = useSearchParams();
  const preset = ['yes', 'no'].includes(params.get('answer')) ? params.get('answer') : '';
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [answer, setAnswer] = useState(preset);
  const [comment, setComment] = useState('');
  const [recorded, setRecorded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const alive = useRef(true);
  // Only the newest load may change what is shown.
  const reqRef = useRef(0);
  const groupId = useId();
  const commentId = useId();

  const load = useCallback(async () => {
    const req = ++reqRef.current;
    setLoadError(null);
    try {
      const d = await api.getWatchEvent(customerId, eventId, notificationId);
      if (alive.current && reqRef.current === req) setData(d);
    } catch (e) {
      if (!alive.current || reqRef.current !== req) return;
      const gone = e?.status === 404 || e?.status === 403;
      setLoadError({ text: gone ? NOT_AVAILABLE : (e?.message || 'The event could not be loaded.'), retry: !gone });
    }
  }, [customerId, eventId, notificationId]);
  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  async function confirm(e) {
    e.preventDefault();
    // An admin who did not receive the notice answers under the stand-in id the portal gave (ENTRAID-347).
    const target = data?.myNotification ?? (data?.adminAnswer?.canAnswer ? data.adminAnswer : null);
    if (!answer || !target || busy) return;
    const asAdmin = !data.myNotification;
    setBusy(true);
    setActionError(null);
    try {
      const trimmed = comment.trim();
      const body = { answer, ...(answer === 'no' && trimmed ? { comment: trimmed } : {}), ...(asAdmin ? { asAdmin: true } : {}) };
      await api.attestWatchNotification(customerId, target.notificationId, body);
      if (alive.current) setRecorded(answer);
      if (asAdmin) await load();
    } catch (err) {
      if (!alive.current) return;
      setActionError(answerError(err));
      // The notice changed under the person (answered elsewhere, expired): show where it stands now.
      if (err?.status === 409 || err?.status === 410) await load();
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function markExpected() {
    setBusy(true);
    setActionError(null);
    try {
      await api.markWatchEventExpected(customerId, eventId);
      if (alive.current) await load();
    } catch (err) {
      if (!alive.current) return;
      setActionError(err?.message || 'The event could not be marked as expected.');
      // Answered, flagged or aged out meanwhile: show where it stands now.
      if (err?.status === 409) await load();
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function openCase() {
    setBusy(true);
    setActionError(null);
    try {
      await api.openWatchCase(customerId, eventId);
      if (alive.current) await load();
    } catch (err) {
      if (alive.current) setActionError(err?.message || 'The case could not be opened.');
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  if (!data) {
    if (loadError) {
      return (
        <div className="card space-y-3">
          <div className="text-ipc-bad" role="alert">{loadError.text}</div>
          {loadError.retry ? <button type="button" className="btn-secondary" onClick={load}>Try again</button> : null}
        </div>
      );
    }
    return <div className="py-12 text-center text-ipc-muted" role="status">Loading the event</div>;
  }
  const ev = data.event;
  const view = data.view ?? null;
  const mine = data.myNotification ?? null;
  // B2: one answer control. A recipient answers their own notice; an administrator who did not receive it gets the
  // stand-in answer when nobody was notified (ENTRAID-347), and Mark as expected / Flag as unexpected otherwise.
  const mode = isAdmin ? adminAnswerMode(data) : (mine ? 'own' : null);
  const adminAns = mode === 'stand-in' ? data.adminAnswer : null;
  const answered = recorded ?? mine?.answer ?? adminAns?.answer ?? null;
  const severity = view?.severity ?? ev.severity;
  const when = view?.when ?? ev.activityDateTime;
  // An admin notice (a record of a change to Watch itself) has no question and opens no case.
  const adminNotice = ev.noticeClass === 'admin-notice';
  const agedOut = ev.answerState === 'aged-out' ? (ev.agedOut ?? {}) : null;
  // "Mark as expected" (admins): the portal decides; a refusal about the person or the event says why.
  const mark = isAdmin ? (data.markExpected ?? null) : null;
  // An answered notice no longer reports canAnswer, so "Recorded" must not depend on it.
  // The same question and answers as the email buttons (shared/watch-question.mjs).
  const q = watchQuestion(ev.catalogId, mine?.audiences ?? (adminAns ? ['admins'] : []));
  const showQuestion = !adminNotice && ((mine && (answered || view?.canAnswer)) || (adminAns && (answered || adminAns.canAnswer)));
  // Mine, unanswered and no longer answerable: say so on load, unless a failed answer already explains it.
  const closed = mine && !answered && !view?.canAnswer && !actionError && !adminNotice;

  return (
    <div className="space-y-6">
      <section className="card space-y-3">
        <SeverityWord severity={severity} />
        <h2 className="break-words">{view?.tenantName ? `${view.tenantName}: ` : ''}{view?.what ?? ev.what}</h2>
        <dl className="grid gap-2 text-sm sm:grid-cols-[10rem_1fr]">
          <dt className="text-ipc-muted">Who</dt><dd className="break-words">{view?.who ?? ev.actor?.displayName ?? 'Unknown actor'}</dd>
          <dt className="text-ipc-muted">When</dt><dd>{formatWhen(when)}</dd>
          {view?.reason ? (<><dt className="text-ipc-muted">Why you received it</dt><dd className="break-words">{view.reason}</dd></>) : null}
          {view?.detected ? (<><dt className="text-ipc-muted">How it was detected</dt><dd className="break-words">{view.detected}</dd></>) : null}
        </dl>
        <WatchGroupLinks links={view?.groupLinks} />
        <WatchEventDetails details={view?.details ?? ev.details} />
        {adminNotice ? (
          <p className="text-sm text-ipc-ink">
            This is a record of a change to Watch itself, kept so that every change has a witness. It has no question to answer.{' '}
            {isAdmin ? <Link className="font-medium text-ipc-light hover:underline" to={notificationsPath(customerId, 'rules')}>Open Rules and audiences</Link> : null}
          </p>
        ) : null}
      </section>

      {adminNotice ? null : (
        <div className="card space-y-2">
          <WatchAnswers answers={ev.answers} resolved={ev.resolved} />
          {(ev.answers ?? []).length === 0 && !ev.resolved && !agedOut ? <p className="text-sm text-ipc-muted">No one has answered yet.</p> : null}
          {agedOut ? (
            <p className="text-sm text-ipc-ink" role="status" data-testid="aged-out">
              <span className="font-medium">Aged out{agedOut.at ? ` on ${formatWhen(agedOut.at)}` : ''}.</span>{' '}
              Nobody answered within {agedOut.days ?? 'the configured number of'} days, so onGuard closed the question. Aged out is not an answer: it is not counted as expected or confirmed.
              {isAdmin ? ' If this change still needs a look, flag it as unexpected below; the aging period is set in Live watch settings.' : ''}
            </p>
          ) : null}
        </div>
      )}

      {showQuestion ? (
        <section className="card space-y-3">
          <h3 id={groupId}>{q.question}</h3>
          {answered ? (
            <div role="status" className="space-y-1">
              <p className="font-medium text-emerald-700">Recorded</p>
              <p className="text-sm text-ipc-ink">Your answer: {answered === 'yes' ? <ExpectedWord>{ANSWER_LABEL.yes}</ExpectedWord> : (ANSWER_LABEL[answered] ?? answered)}</p>
              {adminAns ? <p className="text-sm text-ipc-ink">Answered by {adminAns.answeredBy ?? 'you'} (admin)</p> : null}
              {answered === 'no' ? <p className="text-sm text-ipc-ink">Your security team has been told</p> : null}
            </div>
          ) : (
            <form className="space-y-3" onSubmit={confirm}>
              <div className="flex gap-6" role="radiogroup" aria-labelledby={groupId}>
                {['yes', 'no'].map((v) => (
                  <label key={v} className="flex items-center gap-2 text-sm">
                    <input type="radio" name="answer" value={v} checked={answer === v} onChange={() => setAnswer(v)} />
                    {q[v]}
                  </label>
                ))}
              </div>
              {answer === 'no' ? (
                <div className="text-sm">
                  <label className="label" htmlFor={commentId}>Comment (optional)</label>
                  <textarea id={commentId} className="input" maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)} />
                </div>
              ) : null}
              <button type="submit" className="btn-primary" disabled={!answer || busy}>Confirm</button>
            </form>
          )}
        </section>
      ) : null}

      {adminAns && !answered && !adminAns.canAnswer && !adminNotice ? <p className="card text-sm text-ipc-ink" role="status">{adminAns.reason || CLOSED_TEXT}</p> : null}

      {closed ? <p className="card text-sm text-ipc-ink" role="status">{CLOSED_TEXT}</p> : null}

      {mine ? (
        <section className="card space-y-3">
          <button type="button" aria-expanded={showPrefs} className="text-sm font-medium text-ipc-light hover:underline" onClick={() => setShowPrefs((s) => !s)}>Notification preferences</button>
          {showPrefs ? <Preferences customerId={customerId} notificationId={mine.notificationId} /> : null}
        </section>
      ) : null}

      {isAdmin && data.recipients ? (
        <section className="card space-y-3">
          <h3>Recipients</h3>
          {data.recipients.length === 0 ? <p className="text-sm text-ipc-muted" data-testid="no-recipients">{noRecipientText(ev) ?? 'No one was notified about this event.'}</p> : (
            <ul className="divide-y divide-ipc-border text-sm">
              {data.recipients.map((r) => (
                <li key={r.notificationId} className="flex flex-col gap-1 py-2 sm:flex-row sm:justify-between">
                  <span className="break-all">{r.displayName}, <span>{r.address}</span></span>
                  <span className="text-ipc-ink">{(r.audiences ?? []).map((a) => AUDIENCE_LABEL[a] ?? a).join(', ')}; {statusText(r)}{r.answer === 'yes' ? <>; <ExpectedWord>{ANSWER_LABEL.yes}</ExpectedWord></> : (r.answer ? `; ${ANSWER_LABEL[r.answer] ?? r.answer}` : '')}</span>
                </li>
              ))}
            </ul>
          )}
          {data.recipients.length > 0 && ev.selfUnreached ? <p className="text-sm text-ipc-muted" data-testid="self-unreached">{noRecipientText({ selfUnreached: ev.selfUnreached })}</p> : null}
          {ev.markedExpected ? (
            <p className="text-sm text-ipc-ink" data-testid="marked-expected">
              Answered by {ev.markedExpected.name} (admin), {formatWhen(ev.markedExpected.at)}. Recipients who had not answered are not asked again; any of them can still answer No, which opens a case.
            </p>
          ) : null}
          {data.case ? (
            <Link className="text-sm font-medium text-ipc-light hover:underline" to={activityPath(customerId, `flagged/${encodeURIComponent(data.case.caseId)}`)}>View the case</Link>
          ) : adminNotice || mode !== 'override' ? null : (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-3">
                {mark?.canMark ? <button type="button" className="btn-secondary" disabled={busy} onClick={markExpected}>Mark as expected</button> : null}
                <button type="button" className="btn-secondary" disabled={busy} onClick={openCase}>Flag as unexpected</button>
              </div>
              {mark?.canMark ? (
                <p className="text-xs text-ipc-muted">
                  Mark as expected closes the question for everyone who has not answered and shows it as answered by you (admin). Use it when you know this change was planned. Flag as unexpected opens a case for follow-up.
                </p>
              ) : null}
              {mark && !mark.canMark && mark.reason ? <p className="text-xs text-ipc-muted" data-testid="mark-expected-reason">You cannot mark this event as expected: {mark.reason}</p> : null}
            </div>
          )}
        </section>
      ) : null}

      {loadError ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ipc-bad" role="alert">{loadError.text}</p>
          {loadError.retry ? <button type="button" className="btn-secondary px-3 py-1 text-sm" onClick={load}>Try again</button> : null}
        </div>
      ) : null}
      {actionError ? <p className="text-sm text-ipc-bad" role="alert">{actionError}</p> : null}
    </div>
  );
}
